import { useCallback, useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import { enableWebPushFromUserGesture } from '../../shared/pusNotification/fcm/enableWebPush';
import {
  dismissPushPrompt,
  shouldShowPushPermissionPrompt,
} from '../../shared/pusNotification/fcm/pushPromptState';
import { getPushPermissionState } from '../../shared/pusNotification/fcm/pushPermission';
import './PushPermissionPrompt.css';

/**
 * Recovery banner for logged-in users who accepted cookies but never granted
 * browser notification permission (common on Safari where the prompt only
 * runs from a user gesture and the cookie banner is not shown again).
 */
const PushPermissionPrompt = () => {
  const [visible, setVisible] = useState(false);
  const [isEnabling, setIsEnabling] = useState(false);

  const refreshVisibility = useCallback(() => {
    setVisible(shouldShowPushPermissionPrompt());
  }, []);

  useEffect(() => {
    refreshVisibility();

    const onAuthChange = () => refreshVisibility();
    const onConsentChange = () => refreshVisibility();
    const onFocus = () => refreshVisibility();

    window.addEventListener('localStorageChange', onAuthChange);
    window.addEventListener('storage', onAuthChange);
    window.addEventListener('cookieConsentChanged', onConsentChange);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      window.removeEventListener('localStorageChange', onAuthChange);
      window.removeEventListener('storage', onAuthChange);
      window.removeEventListener('cookieConsentChanged', onConsentChange);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refreshVisibility]);

  const handleEnable = useCallback(async () => {
    setIsEnabling(true);
    try {
      await enableWebPushFromUserGesture();
    } catch (_) {
      // User dismissed the native prompt — non-fatal.
    } finally {
      setIsEnabling(false);
      refreshVisibility();
    }
  }, [refreshVisibility]);

  const handleDismiss = useCallback(() => {
    dismissPushPrompt();
    setVisible(false);
  }, []);

  if (!visible || getPushPermissionState() !== 'default') {
    return null;
  }

  return (
    <div className="push-permission-prompt" role="region" aria-label="Enable notifications">
      <div className="push-permission-prompt__content">
        <p className="push-permission-prompt__text">
          Turn on browser notifications to get alerts for messages and activity, even when this tab
          is in the background.
        </p>
        <div className="push-permission-prompt__actions">
          <Button
            outline
            color="light"
            className="btn-round"
            onClick={handleDismiss}
            disabled={isEnabling}
          >
            Not now
          </Button>
          <Button
            color="danger"
            className="btn-round"
            onClick={handleEnable}
            disabled={isEnabling}
          >
            {isEnabling ? 'Enabling…' : 'Enable notifications'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PushPermissionPrompt;
